import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../stores/authStore';
import { LoginCredentials, RegisterData } from '../services/auth.service';

/**
 * Hook personalizado para autenticación
 * Proporciona funciones y estado de autenticación
 */
export const useAuth = () => {
  const navigate = useNavigate();
  const {
    user,
    isAuthenticated,
    isLoading,
    error,
    login: loginAction,
    logout: logoutAction,
    register: registerAction,
    checkAuth,
    clearError,
  } = useAuthStore();

  // NOTA: La verificación de auth se hace una sola vez en App.tsx
  // No llamar checkAuth aquí para evitar loops infinitos

  /**
   * Inicia sesión y redirige
   */
  const login = useCallback(
    async (credentials: LoginCredentials, redirectTo: string = '/') => {
      try {
        await loginAction(credentials);
        navigate(redirectTo);
      } catch (error) {
        // El error ya está en el store
        throw error;
      }
    },
    [loginAction, navigate]
  );

  /**
   * Cierra sesión y redirige al login
   */
  const logout = useCallback(async () => {
    await logoutAction();
    navigate('/login');
  }, [logoutAction, navigate]);

  /**
   * Registra un usuario y redirige
   */
  const register = useCallback(
    async (data: RegisterData, redirectTo: string = '/') => {
      try {
        await registerAction(data);
        navigate(redirectTo);
      } catch (error) {
        // El error ya está en el store
        throw error;
      }
    },
    [registerAction, navigate]
  );

  /**
   * Verifica si el usuario tiene un rol específico
   */
  const hasRole = useCallback(
    (role: string | string[]): boolean => {
      if (!user) return false;

      if (Array.isArray(role)) {
        return role.includes(user.rol || '');
      }

      return user.rol === role;
    },
    [user]
  );

  /**
   * Verifica si el usuario es administrador
   */
  const isAdmin = useCallback((): boolean => {
    return hasRole(['admin', 'administrador']);
  }, [hasRole]);

  return {
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    logout,
    register,
    checkAuth,
    clearError,
    hasRole,
    isAdmin,
  };
};

export default useAuth;
